import { useCallback, useState, useMemo } from 'react';
import { Inline, InlineItem, Button, Select } from '@kinesis/bungle';
import { noop, isNil } from 'lodash/fp';
import { Identity, Access, AccessControl } from '@/types';
import { identityAvatar } from './identity-avatar';

const accessOptions = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
  // NOTE: these are disabled for now, editor is all powerful
  //  { value: 'sharer', label: 'Sharer' },
  //  { value: 'owner', label: 'Owner' },
];

const AccessControlInput = (props: AccessControlInputProps) => {
  const { candidates, access, onAuthorize = noop } = props;
  const options = useMemo(
    () =>
      candidates
        .filter(
          (candidate: Identity) =>
            !Boolean(
              access.find(
                (a) =>
                  a.identity.type === candidate.type &&
                  a.identity.id === candidate.id,
              ),
            ),
        )
        .map((candidate: Identity) => ({
          value: `${candidate.type}/${candidate.id}`,
          identity: candidate,
          label: candidate.name,
        })),
    [candidates, access],
  );
  const [identityKey, setIdentityKey] = useState<string | undefined>(undefined);
  const [level, setLevel] = useState<Access>('viewer');
  const onAuthorizeCurrent = useCallback(() => {
    const index = Object.fromEntries(
      options.map(({ identity, value }) => [value, identity]),
    );
    const selected = identityKey ? index[identityKey] : undefined;
    if (selected) {
      onAuthorize(selected, level);
      setLevel('viewer');
      setIdentityKey(undefined);
    }
  }, [onAuthorize, level, identityKey, options, setLevel, setIdentityKey]);

  return (
    <Inline>
      <InlineItem sizing='fill-container'>
        <Select
          search
          onChange={setIdentityKey}
          options={options}
          value={identityKey}
          placeholder='Authorize organisation or individual…'
          renderItem={(item: any) => (
            <Inline key={item.value}>
              <InlineItem sizing='fit-content'>
                {identityAvatar(item.identity, 'xsmall')}
              </InlineItem>
              <InlineItem sizing='fill-container'>
                {item.identity.name}
              </InlineItem>
            </Inline>
          )}
        />
      </InlineItem>
      <InlineItem sizing='fit-content'>
        <Select onChange={setLevel} options={accessOptions} value={level} />
      </InlineItem>
      <InlineItem sizing='fit-content'>
        <Button
          variant='action'
          disable={isNil(identityKey)}
          onClick={onAuthorizeCurrent}
        >
          Authorize access
        </Button>
      </InlineItem>
    </Inline>
  );
};

export type AccessControlInputProps = {
  candidates: Identity[];
  access: AccessControl[];
  onAuthorize?: (identity: Identity, access: Access) => void;
};

export { AccessControlInput };
